import { useMemo } from "react";
import { Link } from "react-router-dom";
import { usePageMeta, useJsonLd, breadcrumbJsonLd } from "../lib/seo";

/**
 * Single source of truth for both the rendered page and its FAQPage structured data — Google
 * requires the marked-up answer text to match what a visitor actually sees, so deriving both from
 * one array is what keeps that true as the copy is edited.
 *
 * `answer` is plain text for the JSON-LD; `body` is the rendered version, which may add links.
 * When `body` is omitted the answer text is rendered as-is.
 */
interface Faq {
  question: string;
  answer: string;
  body?: React.ReactNode;
}

const FAQS: Faq[] = [
  {
    question: "What is MarketOnBase?",
    answer:
      "MarketOnBase is a marketplace for digital goods where the buyer's payment is held in a non-custodial smart contract on the Base blockchain until the buyer has what they paid for. The platform never takes custody of the money — it can only move to the buyer or the seller along paths written into the contract.",
  },
  {
    question: "What does it cost?",
    answer:
      "Buyers pay the seller's listed price plus a flat 10% platform fee, itemized before anything is signed. Sellers receive their full listed price; the fee is added on top rather than deducted. The fee rate is fixed in the smart contract at deployment and cannot be changed on an order that is already funded.",
  },
  {
    question: "Is the platform fee refundable?",
    answer:
      "No. The 10% platform fee is charged and paid to the treasury the moment your funding transaction confirms onchain, and it is not returned — including when a dispute later refunds the principal to you in whole or in part. Only the order principal, meaning the seller's listed price, can be released, refunded, or split.",
  },
  {
    question: "Do I need a crypto wallet or a seed phrase?",
    answer:
      "No. Signing in with your email creates a Coinbase-managed Smart Account on Base — no browser extension, no seed phrase, and no private key to store. The account is a real, general-purpose Base account that belongs to you, and MarketOnBase never has access to your keys.",
  },
  {
    question: "Where is my money while an order is in progress?",
    answer:
      "In the smart contract on Base, at a public address you can inspect on a block explorer. It is not in a company bank account, not in a pooled custodial wallet, and not on the platform's balance sheet. The contract has no function that would let the platform withdraw or redirect held principal.",
  },
  {
    question: "What happens if the file isn't what was described?",
    answer:
      "Open a dispute before your order's dispute deadline, which is shown as a countdown on the order page. Opening a dispute pauses release. You submit evidence, the seller can respond, and an arbitrator resolves it by splitting the principal between the two of you — potentially entirely to one side. The contract requires the two shares to add up to exactly the principal, so the arbitrator cannot redirect funds anywhere else.",
  },
  {
    question: "What happens if I do nothing after buying?",
    answer:
      "Once the dispute window passes without you releasing the funds or opening a dispute, the seller can claim the principal themselves. This is deliberate: it means funds don't sit locked forever if a buyer disappears. The countdown is always visible on your order page.",
  },
  {
    question: "Can I get a refund or reverse a payment?",
    answer:
      "Not unilaterally, and not by chargeback. Cryptocurrency transactions are irreversible. Within the dispute window, a dispute resolved in your favour can return the principal to you. After the window closes, or after you have released the funds, the settlement is final.",
  },
  {
    question: "Are the files checked for malware?",
    answer:
      "Yes. Every uploaded file lands in a private quarantine bucket that is not publicly reachable and is scanned for malware before the listing can be published. Only files that pass the scan can be sold. This reduces risk but is not a guarantee — no scanner catches everything, so treat downloads with normal caution.",
  },
  {
    question: "If a seller changes their file after I buy, what do I get?",
    answer:
      "The exact file version that was attached to your order at the time you bought it. Your entitlement is pinned to an immutable snapshot of that version, so a seller replacing the file on their listing afterwards does not change what you receive.",
  },
  {
    question: "What if a payout to my wallet fails?",
    answer:
      "The contract credits the amount to your account to claim later rather than failing the whole settlement — this can happen if, for example, USDC's issuer has blocked an address at that moment. If you have a credited balance waiting, a claim button appears on your wallet page. Nothing pays it out automatically, so you do need to claim it.",
    body: (
      <>
        The contract credits the amount to your account to claim later rather than failing the
        whole settlement — this can happen if, for example, USDC&apos;s issuer has blocked an
        address at that moment. If you have a credited balance waiting, a claim button appears on
        your <Link to="/wallet">wallet page</Link>. Nothing pays it out automatically, so you do
        need to claim it.
      </>
    ),
  },
  {
    question: "Which blockchain and token does this use?",
    answer:
      "USDC on Base. Base is an Ethereum layer-2 network, which keeps transaction fees low, and USDC is a dollar-denominated stablecoin, so prices are quoted in dollars rather than a fluctuating asset.",
  },
  {
    question: "Has the smart contract been audited?",
    answer:
      "It has been through extensive internal review, static analysis, and an automated test suite including fuzz and invariant testing, but it has not had an independent third-party audit. That is a real limitation and we would rather state it plainly. Do not transact with amounts you are not prepared to lose.",
  },
];

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: { "@type": "Answer", text: faq.answer },
  })),
};

export function FaqPage() {
  usePageMeta({
    title: "FAQ",
    description:
      "Answers about fees, disputes, refunds, wallets, malware scanning, and what happens if something goes wrong on MarketOnBase's onchain USDC marketplace.",
    path: "/faq",
  });

  useJsonLd(FAQ_JSONLD);
  useJsonLd(
    useMemo(
      () =>
        breadcrumbJsonLd([
          { name: "Home", path: "/" },
          { name: "FAQ", path: "/faq" },
        ]),
      [],
    ),
  );

  return (
    <article className="stack" style={{ maxWidth: 720 }}>
      <div>
        <h1>Frequently asked questions</h1>
        <p className="muted">
          Fees, disputes, refunds, wallets, and what happens when something goes wrong.
        </p>
      </div>

      <div className="stack">
        {FAQS.map((faq) => (
          <section key={faq.question} className="card stack">
            <h2 style={{ fontSize: "1.05rem" }}>{faq.question}</h2>
            <p>{faq.body ?? faq.answer}</p>
          </section>
        ))}
      </div>

      <section className="stack">
        <h2>Still stuck?</h2>
        <p>
          The <Link to="/how-it-works">how it works</Link> page walks through the full buying and
          selling flow, and <Link to="/about">about</Link> explains the reasoning behind the
          design.
        </p>
      </section>
    </article>
  );
}
